import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AdminService, AdminRecord } from '@/shared/api/rest/services/admin.service'
import { AdminResource } from '@/shared/api/rest/config'
import { useAlertService } from '@/shared/lib/useAlertService'

export function useAdminList(
    resource: AdminResource,
    params: Record<string, string | number | undefined> = {}
): {
    items: AdminRecord[]
    loading: boolean
    error: Error | undefined
    refetch: () => void
} {
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin', resource, params],
        queryFn: () => AdminService.list(resource, params)
    })
    return {
        items: data ?? [],
        loading: isLoading,
        error: error ?? undefined,
        refetch
    }
}

export interface ReferenceOption {
    value: string
    label: string
}

/** Load a resource's records as { value, label } options for reference fields. */
export function useReferenceOptions(
    resource: AdminResource | undefined,
    refLabel: (item: Record<string, unknown>) => string
): { options: ReferenceOption[]; loading: boolean } {
    const { data, isLoading } = useQuery({
        queryKey: ['admin', resource, 'ref-options'],
        queryFn: () => AdminService.list(resource as AdminResource),
        enabled: !!resource
    })
    const options: ReferenceOption[] = (data ?? []).map((item: AdminRecord) => ({
        value: item._id,
        label: refLabel(item)
    }))
    return { options, loading: isLoading }
}

export function useAdminMutations(resource: AdminResource, singular: string) {
    const queryClient = useQueryClient()
    const alertService = useAlertService()

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: ['admin', resource] })

    const create = useMutation({
        mutationFn: (body: Record<string, unknown>) =>
            AdminService.create(resource, body),
        onSuccess: () => {
            alertService.success(`${singular} created`)
            invalidate()
        },
        onError: (e: Error) => alertService.error(e.message)
    })

    const update = useMutation({
        mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
            AdminService.update(resource, id, body),
        onSuccess: () => {
            alertService.success(`${singular} updated`)
            invalidate()
        },
        onError: (e: Error) => alertService.error(e.message)
    })

    const remove = useMutation({
        mutationFn: (id: string) => AdminService.remove(resource, id),
        onSuccess: () => {
            alertService.success(`${singular} deleted`)
            invalidate()
        },
        onError: (e: Error) => alertService.error(e.message)
    })

    return { create, update, remove }
}

export type { AdminRecord }
